import type { IRogue } from '@/js/services/RogueTypes'
import type { OrderError } from '@orders'

export type OrderErrorMessageSpec = { key: string; args?: Record<string, unknown> }

export function getOrderErrorMessage(error: OrderError): OrderErrorMessageSpec {
  switch (error.code) {
    case 'inventory_error':
      return error.sku
        ? { key: 'An item in your cart ({sku}) is not available at the requested quantity.', args: { sku: error.sku } }
        : { key: 'An item in your cart is not available at the requested quantity.' }
    case 'invalid_ship_country':
      return { key: 'Cannot ship to provided shipping address. Please pick a shipping address with a shippable country.' }
    case 'giftcard_balance_error':
      return { key: 'Please re-add the gift card codes to your order before proceeding to Place Order.' }
    case 'wrong_tax_total':
      return { key: 'Tax rates have changed. Please review before proceeding to Place Order.' }
    case 'wrong_shipping_rate':
      return { key: 'Shipping rates have changed. Please review before proceeding to Place Order.' }
    case 'wrong_product_price':
      return { key: 'Product prices may have changed. Please review before proceeding to Place Order.' }
    case 'payment_error':
    case 'apple_error':
    case 'google_error':
      return { key: "There was an error processing your payment. Please <a href='{contactLink}'>contact us</a>, change your payment method, or try again later" }
    case 'paypal_amount_mismatch':
      return { key: 'Your order total does not match the authorized payment amount. Please re-enter your payment information and try again.' }
    case 'invalid_payment_method':
      return { key: "There was an error validating the payment method. Please correct or change your payment method, try again later, or <a href='{contactLink}'>contact us</a>." }
    case 'cvv_required':
      return { key: 'You must enter a CVV number for this purchase.' }
    case 'three_ds':
      return { key: 'Your authorization request has encountered an error. Please try again.' }
    case 'invalid_billing':
      return error.errors?.length
        ? { key: 'There are issues with your billing address. {message}', args: { message: error.errors[0] } }
        : { key: 'There are issues with your billing address.' }
    case 'invalid_shipping':
      return error.errors?.length
        ? { key: 'There are issues with your shipping address. {message}', args: { message: error.errors[0] } }
        : { key: 'There are issues with your shipping address.' }
    case 'shoppay_error':
      return { key: error.errors?.[0] || "An error occurred with Shop Pay. Please try again later." }
    default:
      return { key: "There was an error processing your order. Please <a href='{contactLink}'>contact us</a> or try again later." }
  }
}

export function isExpired(year: number, month: number) {
  return new Date(year, month, 0, 23, 59, 59, 999) < new Date()
}

export function isRedirectPaymentType(adyenType: string | undefined): boolean {
  return !!adyenType && [
    'ideal',
    'paysafecard',
    'mobilepay',
    'trustly',
    'ebanking_fi',
    'eps',
    'klarna_paynow',
    'klarna',
    'klarna_account',
  ].includes(adyenType.toLowerCase())
}

export function getRedirectMessage(adyenType: string | undefined, rogue: IRogue): string | null {
  switch (adyenType) {
    case 'ideal':
      return rogue.__('You will be redirected to the iDEAL | Wero portal after reviewing your order.')
    case 'paysafecard':
      return rogue.__('You will be redirected to the Paysafecard portal after reviewing your order.')
    case 'mobilepay':
      return rogue.__('You will be redirected to the MobilePay portal after reviewing your order.')
    case 'trustly':
      return rogue.__('You will be redirected to the Trustly portal after reviewing your order.')
    case 'ebanking_FI':
    case 'eps':
      return rogue.__('You will be redirected to your bank after reviewing your order.')
    case 'klarna_paynow':
    case 'klarna':
    case 'klarna_account':
      return rogue.__('You will be redirected to the Klarna portal after reviewing your order.')
    default:
      return null
  }
}

export function placeOrderButtonColor(paymentType: string | undefined, adyenType: string | undefined): 'red' | 'black' | 'wero-yellow' | 'trustly-green' | 'mobilepay-blue' | 'paysafecard-blue' | 'klarna-pink' | 'affirm-blue' {
  if (paymentType === 'affirm') {
    return 'affirm-blue'
  }
  
  switch (adyenType) {
    case 'ideal':
      return 'wero-yellow'
    case 'trustly':
      return 'trustly-green'
    case 'mobilepay':
      return 'mobilepay-blue'
    case 'paysafecard':
      return 'paysafecard-blue'
    case 'klarna_paynow':
    case 'klarna':
    case 'klarna_account':
      return 'klarna-pink'
    default:
      return 'red'
  }
}

export function placeOrderButtonLogo(adyenType: string | undefined): string | undefined {
  switch (adyenType) {
    case 'ideal':
      return '/UI/logos/iDEAL_Wero_Lockup_Yellow_Horizontal_RGB_q7dcf8.svg'
    case 'trustly':
      return '/UI/logos/trustly-logo-bright-green.svg'
    case 'mobilepay':
      return '/UI/logos/MP_Logo_Type_Horisontal_White.png'
    case 'paysafecard':
      return '/UI/logos/paysafecard_logo_white.svg'
    case 'klarna_paynow':
    case 'klarna':
    case 'klarna_account':
      return '/UI/logos/klarna-wordmark-black_rt0iw1.png'
    default:
      return undefined
  }
}
